import React, { useEffect, useRef, useState } from "react";
import { MdClose, MdSearch } from "react-icons/md";
import { useDebouncedCallback } from "use-debounce";
import { cx, getControlClassName } from "components/fields/FormField";

/**
 * Search box that keeps its own text and reports it after the user pauses typing.
 *
 * Props:
 * - value="": external value; changing it from outside (e.g. a reset) replaces the text.
 * - onChange(value): called with the debounced string; immediately on clear or Enter.
 * - delay=300: debounce in ms.
 * - placeholder="Search…", ariaLabel="Search", className (wrapper classes), id, name, autoFocus, disabled.
 */
function SearchInput({
  value = "",
  onChange,
  delay = 300,
  placeholder = "Search…",
  className = "",
  ariaLabel = "Search",
  id,
  name,
  autoFocus,
  disabled,
}) {
  const [text, setText] = useState(value ?? "");
  const lastEmitted = useRef(value ?? "");
  const inputRef = useRef(null);

  const emit = (next) => {
    lastEmitted.current = next;
    onChange?.(next);
  };
  const debouncedEmit = useDebouncedCallback(emit, delay);

  useEffect(() => {
    const next = value ?? "";
    if (next !== lastEmitted.current) {
      debouncedEmit.cancel();
      lastEmitted.current = next;
      setText(next);
    }
  }, [value, debouncedEmit]);

  const clear = () => {
    debouncedEmit.cancel();
    setText("");
    emit("");
    inputRef.current?.focus();
  };

  return (
    <div className={cx("relative", className)}>
      <MdSearch
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-muted"
      />
      <input
        ref={inputRef}
        type="search"
        id={id}
        name={name}
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          debouncedEmit(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") debouncedEmit.flush();
          if (e.key === "Escape" && text) {
            e.preventDefault();
            clear();
          }
        }}
        className={getControlClassName({
          className: "pl-10 pr-10 [&::-webkit-search-cancel-button]:appearance-none",
        })}
      />
      {text && !disabled && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-fg-muted hover:bg-surface-subtle hover:text-fg focus-ring"
        >
          <MdClose aria-hidden="true" className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default SearchInput;
